import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, X, Loader2, ImageOff, GripVertical } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useCMS } from "@/hooks/useCMS";
import { SCHEMAS } from "@/pages/admin/schemas";
import { useAuth } from "@/context/AuthContext";
import { MediaPicker } from "@/components/MediaPicker";
import { RichEditor } from "@/components/RichEditor";
import { imgStyle } from "@/lib/imagePos";

function StatusBadge({ value }) {
  const v = value || "published";
  return <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${v === "featured" ? "bg-[#DB1A5C]/15 text-[#DB1A5C]" : v === "draft" ? "bg-muted text-muted-foreground" : "bg-[#CAE295]/40 text-green-800"}`}>{v}</span>;
}

function EditorCard({ it, card, onEdit, onDelete, readOnly, canReorder, idx, dragIndex, setDragIndex, onDropRow }) {
  const img = card.image ? it[card.image] : null;
  const accent = (card.accent && it[card.accent]) || "#2CACD4";
  return (
    <div
      draggable={canReorder}
      onDragStart={() => canReorder && setDragIndex(idx)}
      onDragOver={(e) => canReorder && e.preventDefault()}
      onDrop={() => canReorder && onDropRow(idx)}
      className={`group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:shadow-lg hover:-translate-y-0.5 ${dragIndex === idx ? "opacity-40" : ""}`}
      data-testid="resource-card"
    >
      <div className="relative h-36 w-full overflow-hidden" style={img ? undefined : { backgroundColor: accent }}>
        {img ? (
          <img src={img} alt="" className="h-full w-full object-cover" style={imgStyle(it, card.image)} />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <span className="font-display font-black uppercase text-3xl text-white/90 drop-shadow">{(it[card.title] || "?").slice(0, 2)}</span>
          </div>
        )}
        <span className="absolute left-2.5 top-2.5 h-5 w-5 rounded-full border-2 border-white shadow" style={{ backgroundColor: accent }} />
        {canReorder && (
          <span className="absolute right-2.5 top-2.5 cursor-grab rounded-lg bg-black/40 p-1 text-white active:cursor-grabbing" data-testid="resource-card-drag"><GripVertical size={15} /></span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-4">
        {card.eyebrow && it[card.eyebrow] ? <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{it[card.eyebrow]}</p> : null}
        <p className="font-display font-black uppercase leading-tight">{it[card.title] || "Untitled"}</p>
        {card.subtitle && it[card.subtitle] ? <p className="line-clamp-2 text-sm text-muted-foreground">{it[card.subtitle]}</p> : null}
        <div className="mt-auto flex items-center justify-between pt-3">
          <StatusBadge value={it.status} />
          <div className="flex items-center gap-1">
            <button onClick={() => onEdit(it)} className="rounded-lg p-2 hover:bg-muted cursor-pointer" aria-label="Edit" data-testid="resource-edit"><Pencil size={15} /></button>
            {!readOnly && <button onClick={() => onDelete(it)} className="rounded-lg p-2 text-[#DB1A5C] hover:bg-[#DB1A5C]/10 cursor-pointer" aria-label="Delete" data-testid="resource-delete"><Trash2 size={15} /></button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function HtmlField({ value, onChange }) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [raw, setRaw] = useState("");
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    onChange(text);
    e.target.value = "";
  };
  return (
    <div>
      <p className="mb-1.5 text-xs text-muted-foreground">Format your text with the toolbar — headings, bold, lists, links and images. No code needed.</p>
      <RichEditor value={value} onChange={onChange} />
      <button type="button" onClick={() => setShowAdvanced((v) => !v)} className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-muted-foreground hover:text-foreground transition-colors cursor-pointer" data-testid="html-advanced-toggle">
        {showAdvanced ? "▾" : "▸"} Advanced: import raw HTML
      </button>
      {showAdvanced && (
        <div className="mt-2 rounded-xl border border-border bg-background p-3">
          <label className="inline-block rounded-full bg-muted px-3 py-1.5 text-xs font-bold hover:bg-[#2CACD4] hover:text-slate-950 transition-colors cursor-pointer" data-testid="html-upload-label">
            Upload .html file
            <input type="file" accept=".html,.htm,text/html" onChange={onFile} className="hidden" data-testid="html-upload-input" />
          </label>
          <textarea rows={5} value={raw} onChange={(e) => setRaw(e.target.value)} placeholder="…or paste formatted HTML here" className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#2CACD4]" data-testid="html-import-textarea" />
          <button type="button" onClick={() => { onChange(raw); setShowAdvanced(false); setRaw(""); }} className="mt-2 rounded-full bg-[#0F172A] text-white px-4 py-1.5 text-xs font-bold hover:bg-[#2CACD4] hover:text-slate-950 transition-colors cursor-pointer" data-testid="html-import-load">Load into editor</button>
        </div>
      )}
    </div>
  );
}

function FocalImageField({ value, onChange, pos, onPos, zoom, onZoom }) {
  const boxRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const base = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2CACD4]";
  const setFromEvent = (e) => {
    const r = boxRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = Math.min(100, Math.max(0, Math.round(((e.clientX - r.left) / r.width) * 100)));
    const y = Math.min(100, Math.max(0, Math.round(((e.clientY - r.top) / r.height) * 100)));
    onPos(`${x}% ${y}%`);
  };
  const [px, py] = (pos || "50% 50%").replace(/%/g, "").split(" ").map((n) => Number(n) || 50);
  const z = Number(zoom) || 1;
  return (
    <div>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} placeholder="https://…" data-testid="field-image-url" />
      <MediaPicker onPick={onChange} />
      {value ? (
        <>
          <div
            ref={boxRef}
            onMouseDown={(e) => { setDrag(true); setFromEvent(e); }}
            onMouseMove={(e) => drag && setFromEvent(e)}
            onMouseUp={() => setDrag(false)}
            onMouseLeave={() => setDrag(false)}
            className="relative mt-2 h-44 w-full cursor-crosshair overflow-hidden rounded-xl border border-border select-none"
            data-testid="focal-box"
          >
            <img src={value} alt="preview" draggable={false} className="h-full w-full object-cover pointer-events-none" style={{ objectPosition: `${px}% ${py}%`, transform: `scale(${z})` }} />
            <span className="absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-[#DB1A5C] shadow-md pointer-events-none" style={{ left: `${px}%`, top: `${py}%` }} />
          </div>
          <div className="mt-2 flex items-center gap-3">
            <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">Zoom {z.toFixed(2)}×</span>
            <input type="range" min="1" max="2" step="0.05" value={z} onChange={(e) => onZoom(Number(e.target.value))} className="flex-1 accent-[#2CACD4] cursor-pointer" data-testid="focal-zoom" />
            <button type="button" onClick={() => { onPos("50% 50%"); onZoom(1); }} className="rounded-full bg-muted px-3 py-1 text-xs font-bold hover:bg-muted/70 cursor-pointer" data-testid="focal-reset">Reset</button>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Drag on the image to choose which part stays in view.</p>
        </>
      ) : null}
    </div>
  );
}

function Field({ f, value, onChange, item, setField }) {
  const base = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2CACD4]";
  if (f.type === "html")
    return <HtmlField value={value} onChange={onChange} />;
  if (f.type === "textarea")
    return <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`field-${f.name}`} />;
  if (f.type === "select")
    return (
      <select value={value || f.options[0]} onChange={(e) => onChange(e.target.value)} className={`${base} cursor-pointer`} data-testid={`field-${f.name}`}>
        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  if (f.type === "color")
    return (
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#2CACD4"} onChange={(e) => onChange(e.target.value)} className="h-10 w-12 rounded-lg border border-border cursor-pointer" data-testid={`field-${f.name}`} />
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} placeholder="#2CACD4" />
      </div>
    );
  if (f.type === "image")
    return (
      <FocalImageField
        value={value}
        onChange={onChange}
        pos={item?.[`${f.name}_pos`]}
        onPos={(v) => setField(`${f.name}_pos`, v)}
        zoom={item?.[`${f.name}_zoom`]}
        onZoom={(v) => setField(`${f.name}_zoom`, v)}
      />
    );
  if (f.type === "number")
    return <input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className={base} data-testid={`field-${f.name}`} />;
  return <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`field-${f.name}`} />;
}

export default function ResourceManager() {
  const { collection } = useParams();
  const schema = SCHEMAS[collection];
  const { reload } = useCMS();
  const { user } = useAuth();
  const readOnly = user?.role === "viewer";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // object or null
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const canReorder = !q && !readOnly;

  const onDropRow = async (toIndex) => {
    if (dragIndex === null || dragIndex === toIndex) { setDragIndex(null); return; }
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(toIndex, 0, moved);
    setItems(next);
    setDragIndex(null);
    try {
      await api.put(`/content/${collection}/reorder`, { ids: next.map((i) => i.id) });
      toast.success("Order saved");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      load();
    }
  };

  const load = () => {
    setLoading(true);
    api.get(`/content/${collection}`).then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  };

  useEffect(() => { setQ(""); setEditing(null); if (schema) load(); }, [collection]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!q) return items;
    const t = q.toLowerCase();
    return items.filter((it) => JSON.stringify(it).toLowerCase().includes(t));
  }, [items, q]);

  if (!schema) return <p className="text-muted-foreground">Unknown content type.</p>;

  const openNew = () => {
    const blank = {};
    schema.fields.forEach((f) => { if (f.type === "color") blank[f.name] = "#2CACD4"; if (f.name === "status") blank[f.name] = "published"; });
    setEditing(blank);
  };

  const save = async () => {
    for (const f of schema.fields) {
      if (f.required && !editing[f.name]) { toast.error(`${f.label} is required`); return; }
    }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/content/${collection}/${editing.id}`, editing);
      else await api.post(`/content/${collection}`, editing);
      toast.success(`${schema.singular} saved`);
      setEditing(null);
      load();
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete this ${schema.singular.toLowerCase()}?`)) return;
    try {
      await api.delete(`/content/${collection}/${it.id}`);
      toast.success(`${schema.singular} deleted`);
      load();
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`admin-resource-${collection}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-black uppercase text-3xl">{schema.label}</h1>
          <p className="mt-1 text-muted-foreground text-sm">{items.length} item{items.length === 1 ? "" : "s"}</p>
        </div>
        {!readOnly && (
          <button onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white hover:bg-[#2CACD4] hover:text-slate-950 font-bold px-5 py-2.5 transition-colors cursor-pointer" data-testid="resource-add-btn">
            <Plus size={18} /> New {schema.singular}
          </button>
        )}
      </div>

      <div className="mt-6 relative">
        <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full rounded-full border border-border bg-card pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2CACD4]" data-testid="resource-search" />
      </div>

      {canReorder && <p className="mt-3 text-xs text-muted-foreground">Tip: drag {schema.card ? "cards" : "rows"} to reorder. Clear search to enable reordering.</p>}

      {schema.card ? (
        loading ? (
          <div className="mt-4 flex justify-center py-16"><Loader2 className="animate-spin text-[#2CACD4]" /></div>
        ) : filtered.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-border bg-card px-4 py-12 text-center text-muted-foreground"><ImageOff className="mx-auto mb-2" /> Nothing here yet.</div>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="resource-card-grid">
            {filtered.map((it, idx) => (
              <EditorCard key={it.id} it={it} card={schema.card} onEdit={setEditing} onDelete={remove} readOnly={readOnly} canReorder={canReorder} idx={idx} dragIndex={dragIndex} setDragIndex={setDragIndex} onDropRow={onDropRow} />
            ))}
          </div>
        )
      ) : (
      <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-card">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#2CACD4]" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                {canReorder && <th className="w-8 px-2 py-3" />}
                {schema.columns.map((c) => <th key={c} className="px-4 py-3 font-bold">{c}</th>)}
                <th className="px-4 py-3 text-right font-bold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((it, idx) => (
                <tr
                  key={it.id}
                  className={`hover:bg-muted/40 ${dragIndex === idx ? "opacity-40" : ""}`}
                  data-testid="resource-row"
                  draggable={canReorder}
                  onDragStart={() => canReorder && setDragIndex(idx)}
                  onDragOver={(e) => canReorder && e.preventDefault()}
                  onDrop={() => canReorder && onDropRow(idx)}
                >
                  {canReorder && (
                    <td className="w-8 px-2 py-3 cursor-grab active:cursor-grabbing text-muted-foreground" data-testid="resource-drag-handle"><GripVertical size={16} /></td>
                  )}
                  {schema.columns.map((c) => (
                    <td key={c} className="px-4 py-3 max-w-[240px] truncate">
                      {c === "status" ? (
                        <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${it[c] === "featured" ? "bg-[#DB1A5C]/15 text-[#DB1A5C]" : it[c] === "draft" ? "bg-muted text-muted-foreground" : "bg-[#CAE295]/40 text-green-800"}`}>{it[c] || "published"}</span>
                      ) : (it[c] ?? "—")}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setEditing(it)} className="rounded-lg p-2 hover:bg-muted cursor-pointer" aria-label="Edit" data-testid="resource-edit"><Pencil size={15} /></button>
                      {!readOnly && <button onClick={() => remove(it)} className="rounded-lg p-2 hover:bg-[#DB1A5C]/10 text-[#DB1A5C] cursor-pointer" aria-label="Delete" data-testid="resource-delete"><Trash2 size={15} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={schema.columns.length + (canReorder ? 2 : 1)} className="px-4 py-12 text-center text-muted-foreground"><ImageOff className="mx-auto mb-2" /> Nothing here yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-10" onClick={() => setEditing(null)}>
          <div className={`w-full ${schema.fields.some((f) => f.type === "html") ? "max-w-3xl" : "max-w-lg"} rounded-3xl bg-card shadow-2xl`} onClick={(e) => e.stopPropagation()} data-testid="resource-modal">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <h2 className="font-display font-black uppercase text-xl">{editing.id ? "Edit" : "New"} {schema.singular}</h2>
              <button onClick={() => setEditing(null)} className="rounded-lg p-1.5 hover:bg-muted cursor-pointer" aria-label="Close"><X size={20} /></button>
            </div>
            <div className="max-h-[68vh] overflow-y-auto px-6 py-5 space-y-4">
              {schema.fields.map((f) => (
                <div key={f.name}>
                  <label className="block text-sm font-semibold mb-1.5">{f.label}{f.required && <span className="text-[#DB1A5C]"> *</span>}</label>
                  <Field f={f} value={editing[f.name]} onChange={(v) => setEditing((s) => ({ ...s, [f.name]: v }))} item={editing} setField={(k, v) => setEditing((s) => ({ ...s, [k]: v }))} />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
              <button onClick={() => setEditing(null)} className="rounded-full px-5 py-2.5 font-bold hover:bg-muted transition-colors cursor-pointer">Cancel</button>
              {!readOnly && (
                <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white hover:bg-[#2CACD4] hover:text-slate-950 font-bold px-6 py-2.5 transition-colors cursor-pointer disabled:opacity-60" data-testid="resource-save">
                  {saving ? <Loader2 className="animate-spin" size={16} /> : "Save"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
